import { useState, useEffect, type ReactElement, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Sparkles } from "lucide-react";
import AuthModal from "./AuthModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ProtectedRoute component props
 */
interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * ProtectedRoute Component
 *
 * Wraps routes that require authentication.
 * Shows a loading spinner while checking auth,
 * redirects to home with auth modal if not authenticated.
 */
const ProtectedRoute = ({ children }: ProtectedRouteProps): ReactElement => {
  const [loading, setLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async (): Promise<void> => {
    try {
      await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
      setShowAuthModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthSuccess = (): void => {
    setIsAuthenticated(true);
    setShowAuthModal(false);
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#FFF8F0" }}
      >
        <div className="text-center">
          <Sparkles
            className="w-12 h-12 mx-auto mb-4 animate-spin"
            style={{ color: "var(--st-primary)" }}
          />
          <p style={{ fontFamily: "Quicksand, sans-serif", color: "#8B6F47" }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Show auth modal instead of redirecting
    return (
      <>
        <Navigate to="/home" state={{ from: location }} replace />
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => window.history.back()}
          onSuccess={handleAuthSuccess}
        />
      </>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
